// SosToolkit — 3am physical-coping single-screen surface (PR 2A, Gap 3).
//
// Distinct from the C-SSRS-focused CrisisModal: this is the "I'm about
// to use" / "I can't sleep with RLS" moment. Time-of-day aware, ≤ 2
// taps from anywhere (mounted globally in AppShell). Reuses the
// urge_logs table via { source: 'sos_toolkit' } so analytics can
// answer "did the toolkit prevent the dose?".
//
// Mobile discipline:
//  - Full-screen modal at 375×812, NO scroll. dvh (not vh) so the iOS
//    Safari keyboard never pushes bottom actions off-screen.
//  - Three logical rows in a CSS grid with proportional heights so
//    the breathing animation never collides with the coping menu or
//    the action row.
//  - Bottom actions ≥48px tall, fixed at safe-area-inset-bottom.
//  - prefers-reduced-motion: breathing circle is static, captions
//    still cycle.

import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { X, Wind, Thermometer, Footprints, Eye, Droplet, Phone } from 'lucide-react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import { useReducedMotion } from '../../lib/motion';
import { apiPost } from '../../lib/api';
import { isQueuedOffline } from '../../lib/apiErrors';
import { haptics } from '../../lib/haptics';

interface SosToolkitProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Box-breathing: 4-4-4-4 × 5 reps = 80s, padded to ~90s with a 2s lead-in
// and 8s closer. Cycle = 16s. Total = 90s.
const BREATH_TOTAL_S = 90;
const BREATH_CYCLE_S = 16;

type Phase = 'idle' | 'breathing' | 'breathDone';

export function SosToolkit({ open, onOpenChange }: SosToolkitProps) {
  const toast = useToast();
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState<Phase>('idle');
  const [secondsLeft, setSecondsLeft] = useState(BREATH_TOTAL_S);
  const startRef = useRef<number | null>(null);
  const [logging, setLogging] = useState(false);
  // Phase 8 — pre-expand the first tip so the toolkit looks fully loaded
  // on first paint. The old behaviour (all tips collapsed → blank rows)
  // was getting interpreted as "the toolkit didn't finish loading."
  const initialTipKey = (() => {
    const h = new Date().getHours();
    const tipsForNow = h >= 22 || h < 5 ? NIGHT_TIPS : DAY_TIPS;
    return tipsForNow[0]?.key ?? null;
  })();
  const [expandedTip, setExpandedTip] = useState<string | null>(initialTipKey);

  useEffect(() => {
    if (open) {
      // Phase 8 — re-compute the right "first tip" for the current
      // time-of-day each time the modal opens. A user who opens the
      // toolkit at 4am sees the night tips expanded; at 4pm, the day
      // tips. (Hour can drift across re-opens of a single session.)
      const h = new Date().getHours();
      const tipsForNow = h >= 22 || h < 5 ? NIGHT_TIPS : DAY_TIPS;
      setExpandedTip(tipsForNow[0]?.key ?? null);
      return;
    }
    // Reset state on close so reopening starts fresh.
    const t = window.setTimeout(() => {
      setPhase('idle');
      setSecondsLeft(BREATH_TOTAL_S);
      // expandedTip is re-initialised on next open (above), so we
      // leave it alone here.
    }, 240);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (phase !== 'breathing') return;
    startRef.current = Date.now();
    const id = window.setInterval(() => {
      const elapsed = (Date.now() - (startRef.current ?? Date.now())) / 1000;
      const left = Math.max(0, BREATH_TOTAL_S - Math.floor(elapsed));
      setSecondsLeft(left);
      if (left === 0) {
        window.clearInterval(id);
        haptics.confirm();
        setPhase('breathDone');
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [phase]);

  // Box-breath caption cycles 4-4-4-4.
  const breathCaption = (() => {
    if (!startRef.current) return 'In…';
    const seconds = ((Date.now() - startRef.current) % (BREATH_CYCLE_S * 1000)) / 1000;
    if (seconds < 4) return 'In, slowly.';
    if (seconds < 8) return 'Hold.';
    if (seconds < 12) return 'Out, slowly.';
    return 'Hold.';
  })();

  const isNight = (() => {
    const h = new Date().getHours();
    return h >= 22 || h < 5; // 22:00–04:59 local
  })();

  const tips = isNight
    ? NIGHT_TIPS
    : DAY_TIPS;

  async function logUrge() {
    setLogging(true);
    try {
      await apiPost('/api/urge', { intensityStart: 6, source: 'sos_toolkit' });
      haptics.confirm();
      toast.push('Logged. The wave will pass.');
      onOpenChange(false);
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push('Could not log — try again.', { tone: 'error' });
      } else {
        toast.push('Saved offline — will sync later.');
        onOpenChange(false);
      }
    } finally {
      setLogging(false);
    }
  }

  function textPartner() {
    const partner = (() => {
      try {
        return window.localStorage.getItem('qk.sos.partner_number') ?? '';
      } catch {
        return '';
      }
    })();
    const body = encodeURIComponent("I'm using my SOS toolkit — could you check on me?");
    // `sms:` deep link; opens device messaging app. No server-side SMS.
    window.location.href = partner ? `sms:${partner}?&body=${body}` : `sms:?body=${body}`;
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className="sos-toolkit-content"
          aria-describedby="sos-desc"
          // Phase 8 — backdrop click no longer dismisses the toolkit.
          // Pre-fix, any click that landed on the overlay (or that
          // bubbled to it because an inside button lacked
          // stopPropagation) closed the dialog and the user landed
          // back on Today — interpreted as "every click sends me back."
          // The explicit X button in the header is the only close path.
          // Escape key still closes via Radix default (accessibility).
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <header className="sos-toolkit-header">
            <div>
              <Dialog.Title className="sos-toolkit-title">SOS toolkit</Dialog.Title>
              <Dialog.Description id="sos-desc" className="sos-toolkit-subtitle">
                {isNight ? 'Night mode · the body settles after the work, not before.' : 'Day mode · here is the next 3 minutes.'}
              </Dialog.Description>
            </div>
            <button
              type="button"
              className="sos-toolkit-close"
              onClick={() => onOpenChange(false)}
              aria-label="Close SOS toolkit"
            >
              <Icon icon={X} size={22} />
            </button>
          </header>

          <section className="sos-toolkit-breath" aria-label="Breathing">
            {phase === 'idle' ? (
              <>
                <div className="sos-toolkit-breath-prompt">
                  <Icon icon={Wind} size={20} />
                  <span>Box-breathing · 90 seconds · 4-4-4-4</span>
                </div>
                <Button size="lg" onClick={() => setPhase('breathing')}>
                  Start breathing
                </Button>
              </>
            ) : null}
            {phase === 'breathing' ? (
              <>
                {reduce ? (
                  <div className="sos-toolkit-breath-circle sos-toolkit-breath-circle-static" />
                ) : (
                  <motion.div
                    className="sos-toolkit-breath-circle"
                    animate={{ scale: [0.85, 1.15, 1.15, 0.85, 0.85] }}
                    transition={{
                      duration: BREATH_CYCLE_S,
                      times: [0, 0.25, 0.5, 0.75, 1],
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                )}
                <p className="sos-toolkit-breath-caption" aria-live="polite">
                  {breathCaption}
                </p>
                <p className="sos-toolkit-breath-timer">{secondsLeft}s</p>
              </>
            ) : null}
            {phase === 'breathDone' ? (
              <p className="sos-toolkit-breath-done">
                The brain is calmer than it was 90 seconds ago. Keep going — pick one below.
              </p>
            ) : null}
          </section>

          <section className="sos-toolkit-tips" aria-label="Coping tips">
            <h3 className="sos-toolkit-tips-heading">
              {isNight ? 'Body-first ideas for tonight' : 'Three quick options'}
            </h3>
            <ul className="sos-toolkit-tips-list">
              {tips.map((tip) => (
                <li key={tip.key}>
                  <button
                    type="button"
                    className={`sos-toolkit-tip${expandedTip === tip.key ? ' sos-toolkit-tip-open' : ''}`}
                    onClick={() => setExpandedTip(expandedTip === tip.key ? null : tip.key)}
                    aria-expanded={expandedTip === tip.key}
                  >
                    <Icon icon={tip.icon} size={18} />
                    <span className="sos-toolkit-tip-label">{tip.label}</span>
                  </button>
                  {expandedTip === tip.key ? (
                    <p className="sos-toolkit-tip-body">{tip.body}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <footer className="sos-toolkit-actions">
            <Button
              size="lg"
              block
              onClick={() => void logUrge()}
              loading={logging}
            >
              Log this as an urge — don't dose
            </Button>
            <Button
              variant="secondary"
              size="md"
              block
              onClick={textPartner}
              leftIcon={<Icon icon={Phone} size={16} />}
            >
              Text my person
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface SosTip {
  key: string;
  label: string;
  icon: typeof Wind;
  body: string;
}

const NIGHT_TIPS: SosTip[] = [
  {
    key: 'shower',
    label: 'Hot or cold shower',
    icon: Thermometer,
    body: 'For RLS, alternate 30s hot / 30s cold on the legs. The vasoconstriction shock interrupts the restless sensation for 15–30 minutes — enough to fall asleep.',
  },
  {
    key: 'magnesium',
    label: 'Magnesium glycinate reminder',
    icon: Droplet,
    body: '300–400mg magnesium glycinate (not citrate — different GI effect) about 30 minutes before bed eases RLS for most people in withdrawal. NOT melatonin or Benadryl — both can worsen RLS during kratom withdrawal.',
  },
  {
    key: 'stretch',
    label: 'Calf + hamstring stretch',
    icon: Footprints,
    body: 'Wall calf-stretch 60s each side, then standing forward fold 60s. Combined with the shower above this resolves most RLS episodes for at least an hour.',
  },
];

const DAY_TIPS: SosTip[] = [
  {
    key: 'grounding',
    label: '5-4-3-2-1 grounding',
    icon: Eye,
    body: '5 things you can see. 4 things you can touch (touch them). 3 things you can hear. 2 things you can smell. 1 thing you can taste. Slow. The craving voice loses the floor while you do this.',
  },
  {
    key: 'walk',
    label: '10-minute walk outside',
    icon: Footprints,
    body: 'Door to corner to door. Phone off. Direct sunlight on your face if you can. The combination of motion + daylight + change of scene resolves more cravings than any single coping skill in the literature.',
  },
  {
    key: 'cold-water',
    label: 'Cold water on the face',
    icon: Droplet,
    body: 'Dunk your face in cold water (or hold a cold pack against your eyes) for 30 seconds. The mammalian dive reflex drops your heart rate; the urge sensation usually drops with it.',
  },
];
